import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { resolveMatchedBranch, activeFeedbackForBranch } from './conditional-branch.js';
import { pickScenarioBranch } from './scenario.js';
import { addVersionWith, applyVersionHead, pickVersion, removeVersionWith, writeVersionWith } from './versions.js';
/** A small, FIXED set of structural mistake-pattern trigger keys — deliberately not a rich,
 * generically-configurable threshold vocabulary like GPS/Numerical's lt/lte/gt/gte/range/exact. */
export const ORDERING_STRUCTURAL_TRIGGERS = ['reversed', 'adjacent_swap', 'off_by_one'];
/** Which structural triggers make sense to offer for a given shape. 'off_by_one' (the whole
 * sequence shifted by exactly one slot) is meaningless for circular — a one-slot shift is already
 * correct there (rotation-equivalence), so it can never be a mistake. matchesOrderingTrigger below
 * also independently returns false for it under 'circular' as a safety net (a stale state left
 * over from a shape change must not silently misbehave even if the UI stops offering it). */
export function availableStructuralTriggers(shape) {
    return shape === 'circular' ? ['reversed', 'adjacent_swap'] : ['reversed', 'adjacent_swap', 'off_by_one'];
}
const CUSTOM_TRIGGER_PREFIX = 'custom:';
/** Builds the trigger string for a teacher-authored "custom order" distractor — a specific
 * anticipated-wrong sequence the teacher wants to attach feedback to directly, alongside (not
 * instead of) the fixed structural patterns. Exported so the editor UI constructs the exact same
 * format `matchesOrderingTrigger` parses, rather than duplicating the delimiter convention. */
export function buildCustomOrderTrigger(order) {
    return `${CUSTOM_TRIGGER_PREFIX}${order.join(',')}`;
}
/** Returns null if `trigger` isn't a custom-order trigger. Item ids are crypto.randomUUID()
 * strings (hex + hyphens only), so a bare comma-delimited list is an unambiguous encoding. */
export function parseCustomOrderTrigger(trigger) {
    if (!trigger.startsWith(CUSTOM_TRIGGER_PREFIX))
        return null;
    const rest = trigger.slice(CUSTOM_TRIGGER_PREFIX.length);
    return rest.length > 0 ? rest.split(',') : [];
}
function arraysEqual(a, b) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}
/** All N cyclic rotations of `arr`, starting offsets 0..N-1 (offset 0 is `arr` itself). */
function rotations(arr) {
    return arr.map((_, k) => [...arr.slice(k), ...arr.slice(0, k)]);
}
function isReversed(submitted, canonical, isCircular) {
    const reversedCanonical = [...canonical].reverse();
    return isCircular
        ? rotations(reversedCanonical).some(r => arraysEqual(r, submitted))
        : arraysEqual(reversedCanonical, submitted);
}
function swap(arr, i, j) {
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
}
function isAdjacentSwap(submitted, canonical, isCircular) {
    const n = canonical.length;
    // Circular checks every rotation of the canonical order as a base, since any rotation is
    // itself a fully correct answer — an adjacent swap can happen relative to any of them, not
    // just the authored starting offset.
    const bases = isCircular ? rotations(canonical) : [canonical];
    const pairCount = isCircular ? n : n - 1; // circular adjacency wraps (last, first); linear doesn't
    for (const base of bases) {
        for (let i = 0; i < pairCount; i++) {
            const j = (i + 1) % n;
            if (arraysEqual(submitted, swap(base, i, j)))
                return true;
        }
    }
    return false;
}
/** "Off by one" = the *entire* sequence shifted by exactly one slot, in either direction — e.g.
 * canonical ABCD → BCDA (shift left, A moved to the very end) or DABC (shift right, D moved to the
 * very front). Deliberately NOT "any single item picked up and moved anywhere" (that pattern is
 * mathematically coherent but doesn't map to a real mistake a teacher would think to flag, and it
 * silently overlapped with adjacent-swap at displacement=1). Meaningless for circular — any shift
 * there is already correct (rotation-equivalence), so it can never be a mistake. */
function isOffByOneItem(submitted, canonical, isCircular) {
    if (isCircular)
        return false;
    const n = canonical.length;
    if (n < 2)
        return false;
    const rots = rotations(canonical);
    return arraysEqual(submitted, rots[1]) || arraysEqual(submitted, rots[n - 1]);
}
/** Exported for `orderingReachability`, which enumerates permutations against these same rules
 * rather than restating what each trigger pattern means. */
export function matchesOrderingTrigger(trigger, submitted, canonical, isCircular) {
    if (trigger === 'reversed')
        return isReversed(submitted, canonical, isCircular);
    if (trigger === 'adjacent_swap')
        return isAdjacentSwap(submitted, canonical, isCircular);
    if (trigger === 'off_by_one')
        return isOffByOneItem(submitted, canonical, isCircular);
    const customOrder = parseCustomOrderTrigger(trigger);
    // No rotation/reversal equivalence for custom orders, even in circular mode — it's a literal
    // match against exactly what the teacher typed.
    if (customOrder)
        return arraysEqual(submitted, customOrder);
    return false;
}
/** Whether a teacher-authored custom-order distractor is itself a correct answer (exact match, or
 * any rotation when circular) — it can never fire, since evaluateOrdering's terminal
 * correct-check always wins first regardless of what feedback states exist. Unconditional (not
 * about "another feedback" — an answer either is or isn't correct), unlike the pairwise overlap
 * check below. Mirrors doesGpsFeedbackOverlapAccepted/doesValueFeedbackOverlapAccepted's role for
 * their own blocks. */
export function doesOrderingCustomOrderMatchCorrect(customOrder, items, shape) {
    const canonical = items.map(i => i.id);
    return shape === 'circular'
        ? rotations(canonical).some(r => arraysEqual(r, customOrder))
        : arraysEqual(canonical, customOrder);
}
/** Pairs of currently-active targeted feedback triggers whose match-sets genuinely overlap —
 * either two identical custom orders, or a custom order that also satisfies an actively-configured
 * structural pattern (a custom order that merely happens to match a structural pattern the
 * teacher hasn't set up as its own feedback isn't a collision — there's nothing else to fire
 * alongside it). Structural-vs-structural pairs are never checked: for 3+ items the categories are
 * built to be mutually exclusive (the whole reason off-by-one was redefined away from "any single
 * item relocated"), so no such collision should occur in practice. Mirrors
 * findGpsOverlappingFeedbackPairs/findOverlappingFeedbackPairs's shape — feed the result's
 * .flat() into a Set for an O(1) "is this trigger involved in any collision" lookup. */
export function findOrderingOverlappingFeedbackPairs(triggers, items, shape) {
    const canonical = items.map(i => i.id);
    const isCircular = shape === 'circular';
    const pairs = [];
    for (let i = 0; i < triggers.length; i++) {
        for (let j = i + 1; j < triggers.length; j++) {
            const a = triggers[i];
            const b = triggers[j];
            const customA = parseCustomOrderTrigger(a);
            const customB = parseCustomOrderTrigger(b);
            let collides;
            if (customA && customB)
                collides = arraysEqual(customA, customB);
            else if (customA)
                collides = matchesOrderingTrigger(b, customA, canonical, isCircular);
            else if (customB)
                collides = matchesOrderingTrigger(a, customB, canonical, isCircular);
            else
                collides = false;
            if (collides)
                pairs.push([a, b]);
        }
    }
    return pairs;
}
/**
 * Resolves the effective canonical item-id order — the Ordering analog of Dropdown's
 * `resolveEffectiveOptions`, forked at the one point where a full alternate order can't reuse that
 * function's own resolution shape. No branches in use → `items`' own order, unchanged (byte-
 * identical to every Ordering block before this feature existed).
 *
 * Branches in use but nothing resolves (missing/invalid equation, or a coverage gap) → `null`,
 * deliberately NOT `items`' own base order. Once a teacher turns branching on, `items`' order is
 * only ever a SEED a new branch starts from (see `makeConditionalOrderBranch` in the authoring
 * UI) — it stops being a maintained, reviewed "what's correct" the moment branches are the source
 * of truth, so falling back to it on a gap would silently resurrect content nobody is reviewing in
 * the context of that specific failure. `evaluateOrdering` treats `null` as "no order can be
 * marked correct," the direct analog of `resolveEffectiveOptions` forcing every option to
 * `neutral` — failing loudly (impossible to answer correctly) beats failing open.
 */
/**
 * A follower's canonical order for the scenario in force — the scenario twin of
 * `resolveEffectiveOrder`.
 *
 * `null` is the fail-closed answer, exactly as it is there: a linked block whose scenario has
 * no row has no canonical order, so no arrangement can be correct. Returning the block's own
 * order would mark a student against a scenario they were never shown.
 */
export function resolveScenarioOrder(items, branches, scenarioId) {
    if (!branches || branches.length === 0)
        return items.map(i => i.id);
    const matched = pickScenarioBranch(branches, scenarioId);
    return matched ? matched.order : null;
}
export function resolveEffectiveOrder(items, equation, branches, resolvedVariables, angleMode = 'degrees') {
    if (!branches || branches.length === 0)
        return items.map(i => i.id);
    const matched = resolveMatchedBranch(equation, branches, resolvedVariables, angleMode);
    return matched ? matched.order : null;
}
/** A branch's `order` isn't a valid permutation of the CURRENT item id set — missing an id, has a
 * duplicate, or references a foreign id (e.g. an item added/removed since the branch was set up).
 * Regardless of which value the shared equation resolves to, this branch can never grade
 * correctly if selected — the Ordering analog of `branchRoleIssue`. */
export function orderBranchIssue(branch, items) {
    const itemIds = new Set(items.map(i => i.id));
    if (branch.order.length !== itemIds.size)
        return 'invalid-order';
    const seen = new Set();
    for (const id of branch.order) {
        if (!itemIds.has(id) || seen.has(id))
            return 'invalid-order';
        seen.add(id);
    }
    return null;
}
/**
 * Returns the feedback states that match a student's submitted order.
 *
 * Evaluation sequence (same shape as evaluateMCQ/evaluateGPS/evaluateSlider):
 * 1. optional mode → any_response, touched or not — no correctness check at all for this mode
 *    (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. Untouched (graded/survey only) → unanswered state (mirrors GPS/Slider — the order itself is
 *    always a full permutation, even with zero interaction, so touched is the only valid
 *    no-answer signal).
 * 3. survey mode → any_answer (any submitted order counts as participation).
 * 4. graded mode → exact-match (rotation-equivalent when shape === 'circular') is checked first
 *    and is terminal; only when not correct does the targeted mistake-trigger set get
 *    consulted (all matching triggers fire in parallel), falling back to `incorrect`.
 *
 * `resolvedVariables` resolves `conditionalBranches` (see `ConditionalOrderBranch`) — optional and
 * a no-op whenever `blockData.conditionalBranches` is unset. When a branch resolves, its OWN
 * feedback is what fires, and `effectiveOrder` (from `resolveEffectiveOrder`) replaces `items`'
 * own order for every correctness/structural-mistake check below — same
 * "branch fundamentally changes what's correct, so everything downstream reads through it" shape
 * as `evaluateDropdown`. On a coverage gap, `effectiveOrder` is `null` → treated as `[]`: no
 * submitted order can ever equal it and `rotations([])` is `[]`, so every structural check safely
 * never matches (the block becomes unanswerable for that roll) — a literal custom-order targeted
 * trigger still fires normally, since it's matched directly against its own stored order,
 * independent of `canonical`. Branches are consulted ONLY in `graded` mode, same gate as Dropdown
 * (branch feedback is only ever authored with graded-shaped triggers).
 */
export function evaluateOrdering(answer, blockData, resolvedVariables = {}, 
/** The leader's variant in force — see `evaluateDropdown`'s own note. */
scenarioId) {
    const { touched, order: submitted } = answer;
    const { items, shape, gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches, angleMode } = blockData;
    /* A FOLLOWER'S SCENARIO ROW REPLACES the BLOCK's numeric branch, never joins it. The row's
       OWN nested branching is a different thing — see `rolesForScenarioRow`. */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const effectiveOrder = gradingMode !== 'graded' ? items.map(i => i.id)
        : followed ? resolveScenarioOrder(items, scenarioBranches, scenarioId)
            : resolveEffectiveOrder(items, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    const canonical = effectiveOrder ?? [];
    // Optional chaining, not a plain ternary — a branch saved before `feedback` was added to
    // ConditionalOrderBranch has no such field at all; falling back to the block's own top-level
    // feedback here is safer than crashing on `.states` of `undefined`.
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer always lands on Default Incorrect instead of a leftover targeted state firing
    // terminally on an option that was just declared neutral. See activeFeedbackForBranch.
    const activeFeedback = activeFeedbackForBranch((scenarioRow ?? matchedBranch)?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    /** The targeted states this arrangement fires. ONE matcher, used by every grading mode — see
     *  `participationOutcome`. */
    const targetedForOrder = () => activeFeedback.states.filter(st => st.type === 'targeted'
        && matchesOrderingTrigger(st.trigger, submitted, canonical, shape === 'circular'));
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    if (!touched) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedForOrder());
    }
    const isCircular = shape === 'circular';
    // `canonical.length > 0` guards a coverage gap explicitly rather than relying only on
    // `arraysEqual`'s own length check — `submitted` is documented as always a full permutation
    // (OrderingAnswer's own doc comment), so `canonical` (`[]` on a gap) can never naturally equal
    // it today, but a future caller that doesn't uphold that contract (a student-runtime endpoint
    // accepting a raw payload, say) could otherwise let an empty submission spuriously match an
    // empty canonical. Cheap insurance for the exact property this whole gap-handling design exists
    // to guarantee: correct must stay unreachable, unconditionally, whenever coverage is incomplete.
    const isCorrect = canonical.length > 0 && (isCircular
        ? rotations(canonical).some(r => arraysEqual(r, submitted))
        : arraysEqual(canonical, submitted));
    if (isCorrect) {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForOrder();
    if (matched.length > 0)
        return matched;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
/**
 * An authored order, healed against the item list it is supposed to describe.
 *
 * The two can always disagree, because an order is authored once and the items go on being
 * edited: an id whose item was deleted is dropped, and an item the order never mentioned is kept
 * at the end. Dropping the second would silently lose an item added after the order was set.
 *
 * One function because there were three copies of this line — the custom-order editor, the test
 * widget, and now versions — and three copies of a self-healing rule is how two of them come to
 * heal differently. Branching corrects established the rule; versions reuse it rather than
 * restate it.
 */
export function reconcileOrder(order, items) {
    const validIds = new Set(items.map(i => i.id));
    const named = new Set(order);
    return [...order.filter(id => validIds.has(id)), ...items.filter(i => !named.has(i.id)).map(i => i.id)];
}
export function applyOrderingVersion(data, versionId) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    const byId = new Map(data.items.map(i => [i.id, i]));
    return {
        ...applyVersionHead(data, version),
        items: reconcileOrder(version.order, data.items).map(id => byId.get(id)),
    };
}
export function writeOrderingVersion(current, versionId, next) {
    return writeVersionWith(current, versionId, next, { order: next.items.map(i => i.id) });
}
export function addOrderingVersion(data, fromVersionId, newId) {
    return addVersionWith(data, fromVersionId, newId, { order: data.items.map(i => i.id) });
}
export function removeOrderingVersion(data, versionId) {
    return removeVersionWith(data, versionId, applyOrderingVersion);
}
//# sourceMappingURL=ordering.js.map